import { ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { LoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import type { FeatureFlagKey } from '@app/types'
import { FeatureFlagGateProps, MultiFeatureGateProps } from './FeatureFlagGate.types'

// Most common use case to gate route or feature content based on feature flag
export const FeatureFlagGate = ({ flag, children, fallback = null }: FeatureFlagGateProps) => {
  const { useFeatureFlag, isFeatureFlagsLoading } = useFeatureFlags()
  const isEnabled = useFeatureFlag(flag)

  if (isFeatureFlagsLoading) {
    return <LoadingSpinner />
  }

  return isEnabled ? <>{children}</> : <>{fallback}</>
}

// if we need multiple flags enabled, easy to implement, not sure if we need it.
export const MultiFeatureGate = ({
  flags,
  children,
  fallback = null,
  requireAll = true,
}: MultiFeatureGateProps) => {
  const { useMultipleFeatureFlags, isFeatureFlagsLoading } = useFeatureFlags()
  const flagStates = useMultipleFeatureFlags(flags)

  if (isFeatureFlagsLoading) {
    return <LoadingSpinner />
  }

  const isEnabled = requireAll
    ? flags.every((flag) => flagStates[flag])
    : flags.some((flag) => flagStates[flag])

  return isEnabled ? <>{children}</> : <>{fallback}</>
}

export const FlagGateRoute = ({
  flag,
  fallback,
}: {
  flag: FeatureFlagKey
  fallback: ReactNode
}) => {
  return (
    <FeatureFlagGate flag={flag} fallback={fallback}>
      <Outlet />
    </FeatureFlagGate>
  )
}
